import { formatPrice } from "@/utils/money";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { memo } from "react";

type StoreSellerCartsSummary = SalonStockerHttpTypes.StoreSellerCartsSummary;

export interface CartStatsGridProps {
  /**
   * Summary of filtered/selected carts
   */
  summary: StoreSellerCartsSummary;
  /**
   * Total number of sellers (unfiltered)
   */
  totalSellers: number;
}

/**
 * Displays cart summary statistics in a grid layout.
 *
 * Shows:
 * - Total products count
 * - Grand total price
 * - Number of suppliers (filtered/total)
 */
function CartStatsGridComponent({ summary, totalSellers }: CartStatsGridProps) {
  return (
    // Not `grid-cols-3`: in a 256px rail three equal columns give a 2-digit
    // product count the same width as a formatted price, and the price - the
    // one value here that can't be abbreviated - is what gets clipped. The
    // two counts size to their content and the total takes the slack.
    <div className="grid grid-cols-[auto_1fr_auto] gap-2">
      {/* Products Count */}
      <div className="rounded-lg bg-gray-50 px-2.5 py-3">
        <div className="text-xl font-bold text-gray-900">
          {summary.total_items}
        </div>
        <div className="text-xs text-gray-600">Items</div>
      </div>

      {/* Grand Total */}
      <div className="rounded-lg bg-gray-50 px-2.5 py-3">
        <div className="whitespace-nowrap text-xl font-bold tabular-nums text-gray-900">
          {formatPrice(summary.grand_total)}
        </div>
        <div className="text-xs text-gray-600">Total</div>
      </div>

      {/* Suppliers Count */}
      <div className="rounded-lg bg-gray-50 px-2.5 py-3">
        <div className="whitespace-nowrap text-xl font-bold text-gray-900">
          {summary.total_sellers}
          {summary.total_sellers < totalSellers && (
            <span className="text-sm text-gray-500">/{totalSellers}</span>
          )}
        </div>
        <div className="text-xs text-gray-600">Suppliers</div>
      </div>
    </div>
  );
}

export const CartStatsGrid = memo(CartStatsGridComponent);
